import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@decisiv/ui-components/lib/components/Button';
import Tabs, { Tab } from '@decisiv/ui-components/lib/components/Tabs';

import useAccessPermissionsForm from '../hooks/useAccessPermissionsForm';
import useEditUserForm from '../hooks/useEditUserForm';
import AccessPermissionsTab from './AccessPermissions';
import UserDetailsForm, { FORM_OPTIONS } from './UserDetailsForm';
import UserHeader from './UserHeader';
import { Actions, CancelLink, TabsArea } from './styles';

export const USER_DETAILS_TAB = 'user-details';
export const ACCESS_PERMISSIONS_TAB = 'access-permissions';
const TAB_PARAM = 'tab';

const readActiveTab = () => {
  const requested = new URLSearchParams(window.location.search).get(TAB_PARAM);

  return requested === ACCESS_PERMISSIONS_TAB
    ? ACCESS_PERMISSIONS_TAB
    : USER_DETAILS_TAB;
};

const writeActiveTab = (tab) => {
  const params = new URLSearchParams(window.location.search);

  if (tab === USER_DETAILS_TAB) {
    params.delete(TAB_PARAM);
  } else {
    params.set(TAB_PARAM, tab);
  }

  const search = params.toString();

  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${
      window.location.hash
    }`,
  );
};

/**
 * Everything below the Management shell and the breadcrumb on
 * /management/users/:id/edit.
 *
 * The page's state lives here rather than in EditUser.jsx, and that split is
 * load-bearing: KeyDS LeftNav creates styled components inside a `useMemo`, so
 * its hook count changes between renders and re-rendering it throws "Rendered
 * fewer hooks than expected" on the React 16 pins. Typing re-renders on every
 * keystroke, so the nav has to sit outside it. Same reason UsersContent exists.
 *
 * PageSurface belongs to EditUser so the breadcrumb strip shares its surface.
 */
function EditUserContent({ canEdit, user, usersPath }) {
  const {
    canSave,
    errors,
    isSaving,
    save,
    saved,
    setField,
    values,
  } = useEditUserForm(user);

  const access = useAccessPermissionsForm(user, { canEdit });

  const [activeTab, setActiveTab] = useState(readActiveTab);

  useEffect(() => {
    writeActiveTab(activeTab);
  }, [activeTab]);

  return (
    <>
      <UserHeader
        fullName={saved.fullName}
        lastLoginAt={saved.lastLoginAt}
        // The draft, not `saved`: ticking a role in the tab below has to show its
        // badge here before anything is saved. Falls back to the server's pairs
        // when the tab has no payload to work from.
        permissionAssignments={
          access.isAvailable
            ? access.permissionAssignments
            : saved.permissionAssignments
        }
      />

      <TabsArea>
        <Tabs
          renderHiddenTabsContent={false}
          selectedId={activeTab}
          size="large"
          onChange={setActiveTab}
        >
          <Tab id={USER_DETAILS_TAB} title="User Details">
            <UserDetailsForm
              disabled={!canEdit}
              errors={errors}
              onChange={setField}
              options={user.options}
              username={saved.username}
              values={values}
            />

            {canEdit && (
              <Actions>
                <Button
                  disabled={!canSave}
                  loading={isSaving}
                  loadingText="Saving..."
                  onClick={save}
                  text="Save Changes"
                />
                <CancelLink href={usersPath}>Cancel</CancelLink>
              </Actions>
            )}
          </Tab>

          <Tab id={ACCESS_PERMISSIONS_TAB} title="Access & Permissions">
            <AccessPermissionsTab
              access={access}
              accountAccess={saved.accountAccess}
              canEdit={canEdit}
              email={saved.email}
              fullName={saved.fullName}
              userId={saved.id}
              username={saved.username}
              usersPath={usersPath}
            />
          </Tab>
        </Tabs>
      </TabsArea>
    </>
  );
}

EditUserContent.propTypes = {
  /** False for a viewer holding user-management:Read but not :Modify. */
  canEdit: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    options: FORM_OPTIONS.isRequired,
  }).isRequired,
  usersPath: PropTypes.string.isRequired,
};

EditUserContent.defaultProps = {
  canEdit: false,
};

export default EditUserContent;
